import { InviteStatusType, RoleKey } from '../services/server-types';
import { Role } from '../types/roles';
import { getPredefinedRoles } from '../services/roleService';

export enum TeamTableRowType {
  invite = 'Invite',
  user = 'User',
  team = 'Team',
}

export enum SortDirection {
  asc = 1,
  desc = 0
}

export interface UserNameType {
  firstName: string;
  lastName: string;
}

export type RowDataCommon = {
  roles: string[];
  isPendingOwner?: boolean;
};

export type UserRowData = RowDataCommon & {
  type: TeamTableRowType.user;
  userId: string;
  joinedTeamAt: Date;
  name: UserNameType;
  profileImage: string;
  email: string;
};

export type RoleRowData = Role;

export const isUserRowData = (rowData: TeamMemberTableRowData | { type: TeamTableRowType }): rowData is UserRowData => {
  return rowData.type === TeamTableRowType.user;
};

export type InviteRowData = RowDataCommon & {
  type: TeamTableRowType.invite;
  inviteId: string;
  acceptLink?: string;
  status: InviteStatusType;
  dateCreated: Date;
  email: string;
};

export type TeamRowData = RowDataCommon &{
  type: TeamTableRowType.team;
  accountId: string;
  accountName: string;
  accountImage: string;
};

export const isInviteRowData = (rowData: TeamMemberTableRowData | { type: TeamTableRowType }): rowData is InviteRowData => {
  return rowData.type === TeamTableRowType.invite;
};

export const isInvitePending = (rowData: TeamMemberTableRowData | { type: TeamTableRowType }) => {
  return isInviteRowData(rowData) && rowData.status === InviteStatusType.PENDING;
};

export const isExpiredPending = (rowData: InviteRowData) => {
  return rowData.status === InviteStatusType.EXPIRED;
};

export const isTeamRowData = (rowData: TeamMemberTableRowData | { type: TeamTableRowType }): rowData is TeamRowData => {
  return rowData.type === TeamTableRowType.team;
};

export type TeamMemberTableRowData = UserRowData | InviteRowData | TeamRowData;

export type TeamMembersTableData = TeamMemberTableRowData[];

export type RoleTableRowData = RoleRowData;

export type RolesTableData = RoleRowData[];

export const roleDeprecatedKeyToId = (roleDeprecatedKey: string): string => {
  return getPredefinedRoles().find(predefinedRole => predefinedRole.deprecatedKey === roleDeprecatedKey)?.id;
};

export const roleIdToDeprecatedKey = (roleId: string): string => {
  return getPredefinedRoles().find(predefinedRole => predefinedRole.id === roleId)?.deprecatedKey;
};

export const predefinedRoleKeys = getPredefinedRoles().map(role => role.deprecatedKey);

export const predefinedRoleIds = getPredefinedRoles().map(role => role.id);

export const predefinedRoleIdsAndKeys = getPredefinedRoles().map(role => ({ id: role.id, key: role.deprecatedKey }));

export const prettyTitleMock = {
  [RoleKey.OWNER]: 'Owner',
  [RoleKey.ADMIN]: 'Admin',
  [RoleKey.LIMITED_ADMIN]: 'Limited Admin',
  [RoleKey.WEBSITE_MANAGER]: 'Website Manager',
  [RoleKey.LIMITED_WEBSITE_MANAGER]: 'Limited Website Manager',
  [RoleKey.BACKOFFICE_MANAGER]: 'Back Office manager'
};

export const inviteStatusTypes = [
  InviteStatusType.EXPIRED,
  InviteStatusType.PENDING
];

export enum ContributorStatusType {
  USER = 'USER',
  INVITE = 'INVITE',
  TEAM= 'TEAM'
}

export interface MembersTableState {
  query: string;
  filters: {
    roles: string[];
    status: InviteStatusType;
    namesAndEmails: string;
  };
  sort: {
    column: string;
    direction: SortDirection;
  };
}

export interface RolesTableState {
  query: string;
}

export type RoleData = {
  id: string;
  label: string;
  description: string;
  isCustom?: boolean;
};

export enum MembersColumnTypes {
  NAME = 'NAME',
  ROLE = 'ROLE',
  DATE = 'DATE'
}
